import React, { useState } from 'react';
import { CalendarDays, Clock3, X } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from './ui/calendar/Popover';
import { Calendar } from './ui/calendar/Calendar';
import { TimePicker } from './ui/calendar/TimePicker';
import {
  formatDateInputValue,
  formatDateTimeLocalValue,
  parseDateInputValue,
  parseDateTimeLocalValue,
} from '../lib/dateInputs.js';

const cn = (...inputs) => twMerge(clsx(inputs));

export function DateTimeField({
  type = 'date',
  value,
  onChange,
  className,
  disabled,
  required,
  name,
  placeholder,
  min,
  max,
  futureOnly = true,
}) {
  const isDateTime = type === 'datetime-local';
  const selected = isDateTime ? parseDateTimeLocalValue(value) : parseDateInputValue(value);
  const now = new Date();
  const parsedMin = isDateTime ? parseDateTimeLocalValue(min) : parseDateInputValue(min);
  const parsedMax = isDateTime ? parseDateTimeLocalValue(max) : parseDateInputValue(max);
  const futureFloor = isDateTime ? now : parseDateInputValue(now);

  const minBoundary =
    futureOnly && futureFloor
      ? parsedMin && parsedMin > futureFloor
        ? parsedMin
        : futureFloor
      : parsedMin;
  const maxBoundary = parsedMax;

  const [isOpen, setIsOpen] = useState(false);

  const emitValue = (nextDate) => {
    const nextValue = nextDate
      ? isDateTime
        ? formatDateTimeLocalValue(nextDate)
        : formatDateInputValue(nextDate)
      : '';

    onChange?.({ target: { value: nextValue, name } });
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const nextDate = new Date(date);
    if (selected) {
      nextDate.setHours(selected.getHours());
      nextDate.setMinutes(selected.getMinutes());
    } else {
      nextDate.setHours(12);
      nextDate.setMinutes(0);
    }
    nextDate.setSeconds(0);
    nextDate.setMilliseconds(0);
    emitValue(nextDate);
    if (!isDateTime) {
      setIsOpen(false);
    }
  };

  const handleTimeChange = (hours, minutes) => {
    const nextDate = selected ? new Date(selected) : new Date();
    nextDate.setHours(hours);
    nextDate.setMinutes(minutes);
    nextDate.setSeconds(0);
    nextDate.setMilliseconds(0);
    emitValue(nextDate);
  };

  const displayValue = selected
    ? format(selected, isDateTime ? 'dd MMM yyyy, HH:mm' : 'dd MMM yyyy')
    : '';

  const hasValue = Boolean(value);
  const allowClear = !required && !disabled;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <span className="pointer-events-none absolute inset-y-0 left-3 z-10 flex items-center text-muted-foreground">
            {isDateTime ? <Clock3 className="h-4 w-4" /> : <CalendarDays className="h-4 w-4" />}
          </span>
          <input
            type="text"
            readOnly
            value={displayValue}
            onClick={() => !disabled && setIsOpen(true)}
            placeholder={placeholder || (isDateTime ? 'Select date and time' : 'Select date')}
            disabled={disabled}
            className={cn(
              className,
              'mm-date-picker-trigger w-full rounded-xl border border-input bg-background py-2 pr-20 pl-10 text-sm text-foreground outline-none transition-all focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 text-left cursor-pointer'
            )}
          />
          {allowClear && hasValue ? (
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                emitValue(null);
              }}
              className="absolute inset-y-0 right-10 z-10 flex items-center text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
              aria-label="Clear date"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          <span className="pointer-events-none absolute inset-y-0 right-3 z-10 flex items-center text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 flex flex-row" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleDateSelect}
          disabled={(date) => {
            if (minBoundary) {
              const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const m = new Date(minBoundary.getFullYear(), minBoundary.getMonth(), minBoundary.getDate());
              if (d < m) return true;
            }
            if (maxBoundary) {
              const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const mx = new Date(maxBoundary.getFullYear(), maxBoundary.getMonth(), maxBoundary.getDate());
              if (d > mx) return true;
            }
            return false;
          }}
          initialFocus
        />
        {isDateTime && (
          <TimePicker value={selected} onChange={handleTimeChange} />
        )}
      </PopoverContent>
    </Popover>
  );
}

